// SCR-STAFF-506 — کارهای معوق (US-CLN-081): یادداشت‌های امضانشده (معوق > ۷۲ ساعت — AC-CLN-022.3) و وضعیت‌های بالینی نیازمند بازبینی با پیوند،
// کارهای MED (نسخه پیش‌نویس، نتیجه برای بازبینی با نشان بحرانی، درخواست دیرکرده، ارجاع ورودی و پاسخ — AP-MED-01)، منابع آتی (تأیید سرپرست،
// پیگیری عوارض) با نشان «به‌زودی» (C-AGENT-656) و گزارش یادداشت‌های معوق برای مدیر (cln.note.void). AP-CLN-04: صف «در انتظار تأیید من»
// (US-CLN-023؛ cln.note.cosign) با پیوند به مواجهه برای تأیید یا بازگرداندن.
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { unwrap, type ClnCosignQueueItem, type ClnOverdueNoteItem, type ClnPendingTaskItem } from '@phoenix/api-client';
import { useAccess, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { DataTable, ListPage, StatusChip, toJalaliDateTime, useDisplaySettings, type DataTableColumn } from '@phoenix/ui';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link as RouterLink } from 'react-router';
import { clnKeys, encounterPath, remindersPath, SCR_STAFF_506 } from './common';
import { ADVERSE_PATH, planPath } from './beautyCommon';
import { RiskFollowUpDialog } from './PsychPanels';

export function PendingTasksPage() {
  const { t } = useTranslation('cln');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const { timeZone } = useDisplaySettings();
  const tasks = useQuery({ queryKey: clnKeys.pending(session?.tenantId), queryFn: () => unwrap(tenantApi.GET('/api/v1/clinical/my/pending-tasks')) });
  const canReport = access.can('cln.note.void');
  // AP-CLN-03 (BR-CLN-112): پیگیری بالینی ارزیابی خطر از همین صفحه بسته می‌شود.
  const [followUp, setFollowUp] = useState<string | null>(null);
  const canFollowUp = access.accessMode === 'Full' && access.can('cln.risk.assess');
  const overdue = useQuery({
    queryKey: clnKeys.overdue(session?.tenantId),
    enabled: canReport,
    queryFn: () => unwrap(tenantApi.GET('/api/v1/clinical/reports/overdue-notes')),
  });
  const canCosign = access.can('cln.note.cosign');
  const cosignQueue = useQuery({
    queryKey: [...clnKeys.pending(session?.tenantId), 'cosign'],
    enabled: canCosign,
    queryFn: () => unwrap(tenantApi.GET('/api/v1/clinical/my/cosign-queue')),
  });
  const time = (value: string) => toJalaliDateTime(value, { timeZone });
  const cosignColumns: DataTableColumn<ClnCosignQueueItem>[] = [
    { id: 'patient', headerKey: 'cln:common.patient', cell: (row) => row.patientName ?? '—' },
    { id: 'author', headerKey: 'cln:pending.author', cell: (row) => row.authorName ?? '—' },
    { id: 'submitted', headerKey: 'cln:cosign.submitted_at', cell: (row) => time(row.submittedAt) },
    { id: 'state', headerKey: 'cln:pending.state', cell: (row) => (row.overdue ? <StatusChip tone="error" labelKey="cln:pending.overdue" /> : <StatusChip tone="pending" labelKey="cln:pending.open_state" />) },
    {
      id: 'go',
      headerKey: 'cln:common.actions',
      cell: (row) => (
        <Link component={RouterLink} to={encounterPath(row.encounterId)} data-testid="cln-cosign-open">
          {t('cln:cosign.review')}
        </Link>
      ),
    },
  ];
  const columns: DataTableColumn<ClnPendingTaskItem>[] = [
    {
      id: 'kind',
      headerKey: 'cln:pending.kind',
      cell: (row) => (
        <span>
          {t(`cln:pending.kinds.${row.kind}`)} {row.critical ? <Chip size="small" color="error" label={t('cln:pending.critical')} data-testid="cln-pending-critical" /> : null}
        </span>
      ),
    },
    { id: 'patient', headerKey: 'cln:common.patient', cell: (row) => row.patientName ?? '—' },
    {
      id: 'detail',
      headerKey: 'cln:pending.detail',
      cell: (row) => (row.statusKind ? t(`cln:status_kind.${row.statusKind}`) : row.encounterId ? t('cln:pending.note') : '—'),
    },
    { id: 'since', headerKey: 'cln:pending.since', cell: (row) => time(row.since) },
    {
      id: 'overdue',
      headerKey: 'cln:pending.state',
      cell: (row) => (row.overdue ? <StatusChip tone="error" labelKey="cln:pending.overdue" /> : <StatusChip tone="pending" labelKey="cln:pending.open_state" />),
    },
    {
      id: 'go',
      headerKey: 'cln:common.actions',
      cell: (row) =>
        // AP-CLN-02: پیگیری عارضه ← جزئیات عارضه؛ عقب از برنامه ← صفحه طرح. AP-CLN-03: پیگیری خطر ← دیالوگ بستن پیگیری.
        row.kind === 'risk_followup' && row.sourceId && canFollowUp ? (
          <Button size="small" onClick={() => setFollowUp(row.sourceId ?? null)} data-testid="cln-pending-risk">
            {t('cln:psych.complete_follow_up')}
          </Button>
        ) : (row.kind === 'adverse_followup' || row.kind === 'plan_behind_schedule') && row.sourceId ? (
          <Link component={RouterLink} to={row.kind === 'adverse_followup' ? `${ADVERSE_PATH}?id=${row.sourceId}` : planPath(row.sourceId)} data-testid="cln-pending-go">
            {t('cln:pending.go')}
          </Link>
        ) : row.kind === 'result_review' || row.kind === 'referral_incoming' || row.kind === 'referral_response' ? (
          <Link component={RouterLink} to={row.kind === 'result_review' ? '/desk/orders' : '/desk/referrals'} data-testid="cln-pending-go">
            {t('cln:pending.go')}
          </Link>
        ) : row.kind === 'clinical_reminder' ? (
          <Link component={RouterLink} to={remindersPath(row.patientId)} data-testid="cln-pending-reminder">
            {t('cln:pending.go')}
          </Link>
        ) : row.encounterId ? (
          <Link component={RouterLink} to={encounterPath(row.encounterId)} data-testid="cln-pending-go">
            {t('cln:pending.go')}
          </Link>
        ) : (
          <Link component={RouterLink} to={`/patients/${row.patientId}?tab=clinical`} data-testid="cln-pending-go">
            {t('cln:pending.go')}
          </Link>
        ),
    },
  ];
  const overdueColumns: DataTableColumn<ClnOverdueNoteItem>[] = [
    { id: 'file', headerKey: 'cln:pending.file', cell: (row) => <bdi dir="ltr">{row.fileNumber ?? '—'}</bdi> },
    { id: 'author', headerKey: 'cln:pending.author', cell: (row) => row.authorName ?? '—' },
    { id: 'started', headerKey: 'cln:pending.started', cell: (row) => time(row.encounterStartedAt) },
    { id: 'hours', headerKey: 'cln:pending.hours', cell: (row) => t('cln:pending.hours_value', { hours: row.hoursOverdue }) },
  ];
  return (
    <ListPage screenId={SCR_STAFF_506} titleKey="cln:pending.title" subtitleKey="cln:pending.subtitle" badges={tasks.data ? <Chip label={tasks.data.count} color="error" size="small" data-testid="cln-pending-total" /> : undefined}>
      <DataTable
        captionKey="cln:pending.title"
        columns={columns}
        rows={tasks.data?.items}
        getRowId={(row) => `${row.kind}-${row.sourceId ?? row.noteId ?? row.statusId ?? row.patientId}`}
        isLoading={tasks.isPending}
        error={tasks.error}
        onRetry={() => void tasks.refetch()}
        emptyTitleKey="cln:pending.empty"
      />
      {(tasks.data?.pendingSources ?? []).length > 0 && (
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 2 }} data-testid="cln-pending-sources">
          <Typography variant="body2" color="text.secondary">
            {t('cln:pending.sources')}
          </Typography>
          {(tasks.data?.pendingSources ?? []).map((source) => (
            <Chip key={source} size="small" variant="outlined" label={`${t(`cln:pending.source.${source}`)} · ${t('cln:common.soon')}`} />
          ))}
        </Stack>
      )}
      {canCosign && (
        <Paper variant="outlined" sx={{ p: 2, mt: 3 }} data-testid="cln-cosign-queue">
          <Typography variant="h3" component="h2" sx={{ mb: 1 }}>
            {t('cln:cosign.queue')}
          </Typography>
          <DataTable
            captionKey="cln:cosign.queue"
            columns={cosignColumns}
            rows={cosignQueue.data?.items}
            getRowId={(row) => row.noteId}
            isLoading={cosignQueue.isPending}
            error={cosignQueue.error}
            onRetry={() => void cosignQueue.refetch()}
            emptyTitleKey="cln:cosign.queue_empty"
          />
        </Paper>
      )}
      {canReport && (
        <Paper variant="outlined" sx={{ p: 2, mt: 3 }} data-testid="cln-overdue-report">
          <Typography variant="h3" component="h2" sx={{ mb: 1 }}>
            {t('cln:pending.overdue_report')}
          </Typography>
          <DataTable
            captionKey="cln:pending.overdue_report"
            columns={overdueColumns}
            rows={overdue.data?.items}
            getRowId={(row) => row.noteId}
            isLoading={overdue.isPending}
            error={overdue.error}
            onRetry={() => void overdue.refetch()}
            emptyTitleKey="cln:pending.overdue_empty"
          />
        </Paper>
      )}
      {followUp && <RiskFollowUpDialog assessmentId={followUp} onClose={() => setFollowUp(null)} />}
    </ListPage>
  );
}
